import { motion } from 'framer-motion';
import { Sun, Moon } from 'lucide-react';
import { AnimatedLogo } from './AnimatedLogo';
import type { Page } from '../App';

interface HeaderProps {
  currentPage: Page;
  setCurrentPage: (page: Page) => void;
  isDarkMode: boolean;
  toggleDarkMode: () => void;
}

export function Header({ currentPage, setCurrentPage, isDarkMode, toggleDarkMode }: HeaderProps) {
  const navItems: { id: Page; label: string }[] = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'projects', label: 'Projects' },
    { id: 'mindfork', label: 'MindFork' },
    { id: 'contact', label: 'Contact' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-nav">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <motion.div 
            className="flex items-center space-x-3 cursor-pointer"
            onClick={() => setCurrentPage('home')}
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
          >
            <AnimatedLogo size={40} />
            <span className="font-['Spectral'] text-2xl tracking-wide">
              CORDALUX
            </span>
          </motion.div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <motion.button
                key={item.id}
                onClick={() => {
                  // MindFork routes to external domain
                  if (item.id === 'mindfork') {
                    window.location.href = 'https://mindfork.me';
                  } else {
                    setCurrentPage(item.id);
                  }
                }}
                className={`relative font-inter tracking-wider transition-all duration-300 ${
                  currentPage === item.id
                    ? 'text-[#FFD700] glow-gold-sm'
                    : isDarkMode
                    ? 'text-gray-300 hover:text-white hover:glow-gold-sm'
                    : 'text-gray-700 hover:text-black hover:glow-gold-sm'
                }`}
                whileHover={{ scale: 1.1, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                {item.label}
                {currentPage === item.id && item.id !== 'mindfork' && (
                  <motion.div
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-[#FFD700] to-purple-400 rounded-full"
                    layoutId="activeTab"
                    initial={{ opacity: 0, scaleX: 0 }}
                    animate={{ opacity: 1, scaleX: 1 }}
                    transition={{ duration: 0.4, type: "spring", stiffness: 300 }}
                  />
                )}
              </motion.button>
            ))}
          </nav>

          {/* Dark Mode Toggle */}
          <motion.button
            onClick={toggleDarkMode}
            className={`p-3 rounded-full glass-subtle transition-all duration-300 ${
              isDarkMode
                ? 'text-[#FFD700] hover-glow-gold'
                : 'text-amber-600 hover-glow-gold'
            }`}
            whileHover={{ scale: 1.1, rotate: 180 }}
            whileTap={{ scale: 0.9 }}
            transition={{ duration: 0.5, type: "spring", stiffness: 200 }}
          >
            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
          </motion.button>
        </div>
      </div>
    </header>
  );
}
